import { MessageSquare } from 'lucide-react';
import { useTracker } from '@/lib/tracker';
import {
  BAND_TONE,
  SEVERITY_TONE,
  STATUS_LABEL,
  bandOf,
  isOpen,
  partyLabel,
  useOpenRaid,
  type RaidItem,
} from '@/lib/raid';
import type { Program } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { Badge } from '@/components/ui/misc';
import { PhaseChip } from '@/components/tracker/bits';

export function RaidRating({ i }: { i: RaidItem }) {
  if (i.type === 'RISK' && i.score)
    return (
      <Badge tone={BAND_TONE[bandOf(i.score)]} title={`Likelihood ${i.likelihood} x impact ${i.impact}`}>
        {i.score} {bandOf(i.score).toLowerCase()}
      </Badge>
    );
  if (i.type === 'ISSUE' && i.severity)
    return <Badge tone={SEVERITY_TONE[i.severity]}>{i.severity.toLowerCase()}</Badge>;
  return <span className="text-muted">-</span>;
}

export function RaidStatusBadge({ s }: { s: RaidItem['status'] }) {
  const tone = s === 'OPEN' ? 'info' : s === 'MITIGATING' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{STATUS_LABEL[s]}</Badge>;
}

export function RaidTable({
  program,
  items,
  showPhase = true,
  empty = 'Nothing here.',
}: {
  program: Program;
  items: RaidItem[];
  showPhase?: boolean;
  empty?: string;
}) {
  const open = useOpenRaid();
  const tracker = useTracker(program.id);
  const phase = (id: string | null) => tracker.data?.phases.find((p) => p.id === id);
  const today = new Date().toISOString().slice(0, 10);

  if (!items.length) return <p className="px-4 py-8 text-center text-sm text-muted">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
          <tr>
            <th className="w-16 px-4 py-2 font-medium">Ref</th>
            <th className="px-2 py-2 font-medium">Title</th>
            <th className="px-2 py-2 font-medium">Owner</th>
            <th className="px-2 py-2 font-medium">Status</th>
            <th className="px-2 py-2 font-medium">Score or severity</th>
            <th className="px-2 py-2 font-medium">Target</th>
            {showPhase && <th className="px-4 py-2 font-medium">Phase</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((i) => {
            const ph = phase(i.phaseId);
            const late = isOpen(i) && i.targetDate && i.targetDate < today;
            return (
              <tr
                key={i.id}
                onClick={() => open(i.id)}
                className={cn(
                  'cursor-pointer border-b border-border last:border-0 hover:bg-surface-2/60',
                  !isOpen(i) && 'text-muted',
                )}
              >
                <td className="px-4 py-2 font-mono text-xs text-muted">{i.ref}</td>
                <td className="px-2 py-2">
                  <button
                    type="button"
                    className="text-left font-medium hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      open(i.id);
                    }}
                  >
                    {i.title}
                  </button>
                  {i.comments > 0 && (
                    <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-muted">
                      <MessageSquare className="size-3" />
                      {i.comments}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2">{i.ownerName ?? partyLabel(i.ownerParty, program)}</td>
                <td className="px-2 py-2">
                  <RaidStatusBadge s={i.status} />
                </td>
                <td className="px-2 py-2">
                  <RaidRating i={i} />
                </td>
                <td className={cn('whitespace-nowrap px-2 py-2', late && 'font-medium text-bad')}>
                  {i.targetDate ? fmtDate(i.targetDate) : ''}
                  {late && <span className="sr-only"> (past target)</span>}
                </td>
                {showPhase && (
                  <td className="px-4 py-2">
                    {ph ? <PhaseChip number={ph.number} /> : <span className="text-muted">Program</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** 5 x 5 likelihood-by-impact grid of open risks. Clicking a cell filters the list. */
export function HeatMap({
  items,
  selected,
  onSelect,
}: {
  items: RaidItem[];
  selected: { l: number; i: number } | null;
  onSelect: (c: { l: number; i: number } | null) => void;
}) {
  const risks = items.filter((x) => x.type === 'RISK' && isOpen(x) && x.likelihood && x.impact);
  const count = (l: number, i: number) => risks.filter((r) => r.likelihood === l && r.impact === i).length;
  const fill = { LOW: 'bg-ok-soft', MEDIUM: 'bg-warn-soft', HIGH: 'bg-bad-soft' };
  const ink = { LOW: 'text-ok', MEDIUM: 'text-warn', HIGH: 'text-bad' };
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center justify-center">
        <span className="-rotate-90 whitespace-nowrap text-[11px] uppercase tracking-wide text-muted">Impact</span>
      </div>
      <div>
        <div
          className="grid grid-cols-[16px_repeat(5,44px)] gap-1"
          role="grid"
          aria-label="Open risks by likelihood and impact"
        >
          {[5, 4, 3, 2, 1].map((imp) => (
            <div key={imp} role="row" className="contents">
              <span className="flex items-center justify-center text-[11px] text-muted">{imp}</span>
              {[1, 2, 3, 4, 5].map((l) => {
                const n = count(l, imp);
                const b = bandOf(l * imp);
                const on = selected?.l === l && selected?.i === imp;
                return (
                  <button
                    key={l}
                    type="button"
                    role="gridcell"
                    aria-label={`Likelihood ${l}, impact ${imp}: ${n} open risk${n === 1 ? '' : 's'}`}
                    aria-pressed={on}
                    disabled={!n && !on}
                    onClick={() => onSelect(on ? null : { l, i: imp })}
                    className={cn(
                      'h-9 rounded text-sm font-semibold tabular-nums transition',
                      fill[b],
                      n ? ink[b] : 'text-transparent',
                      n && 'hover:ring-2 hover:ring-border-strong',
                      on && 'ring-2 ring-text',
                    )}
                  >
                    {n || ''}
                  </button>
                );
              })}
            </div>
          ))}
          <span />
          {[1, 2, 3, 4, 5].map((l) => (
            <span key={l} className="text-center text-[11px] text-muted">
              {l}
            </span>
          ))}
        </div>
        <p className="mt-1 text-center text-[11px] uppercase tracking-wide text-muted">Likelihood</p>
      </div>
    </div>
  );
}
